'use client'

import posthog from 'posthog-js'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { hasAnalyticsConsent } from '@/lib/cookie-consent'
import {
	extractAndSaveAuthFromUrl,
	isUserDataValid,
	parseUserDataCookie,
	redirectToLogin,
	refreshJWTTokens,
	signOutJWT,
	type UserDataCookie,
	verifyJWTWithAuthService,
} from '@/lib/jwt-auth'
import { isInternalAnalyticsUser } from '../../../../shared-types/posthog-privacy'

interface AuthContextValue {
	user: UserDataCookie | null
	loading: boolean
	error: string | null
	isAuthenticated: boolean
	signOut: () => Promise<void>
	refresh: () => Promise<void>
	login: (returnUrl?: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
	children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const [user, setUser] = useState<UserDataCookie | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	const isAuthenticated = isUserDataValid(user)

	const loadUserFromCookie = useCallback(() => {
		try {
			const userData = parseUserDataCookie()

			if (isUserDataValid(userData)) {
				setUser(userData)
				setError(null)
				return userData
			} else {
				setUser(null)
			}
		} catch (error) {
			console.error('Failed to load user from cookie:', error)
			setUser(null)
			setError('Failed to load user session')
		}

		return null
	}, [])

	const verifyWithAuthService = useCallback(async (backgroundMode = false) => {
		try {
			setError(null)
			const jwtPayload = await verifyJWTWithAuthService()

			if (jwtPayload) {
				const userData: UserDataCookie = {
					userId: jwtPayload.userId,
					email: jwtPayload.email,
					role: jwtPayload.role,
					permissions: jwtPayload.permissions,
					expiresAt: jwtPayload.exp * 1000,
				}
				setUser(userData)
				return userData
			} else if (!backgroundMode) {
				setUser(null)
			}
		} catch (error) {
			console.error('JWT verification failed:', error)
			if (!backgroundMode) {
				setError(error instanceof Error ? error.message : 'Authentication failed')
				setUser(null)
			} else {
				console.debug('Background JWT verification failed, keeping local user data')
			}
		}

		return null
	}, [])

	const signOut = useCallback(async () => {
		try {
			setError(null)
			setLoading(true)

			await signOutJWT()
			setUser(null)

			window.location.href = '/'
		} catch (error) {
			console.error('Sign out error:', error)
			setError(error instanceof Error ? error.message : 'Sign out failed')
			setUser(null)
		} finally {
			setLoading(false)
		}
	}, [])

	const refresh = useCallback(async () => {
		try {
			setError(null)
			setLoading(true)

			const success = await refreshJWTTokens()

			if (success) {
				loadUserFromCookie()
			} else {
				setUser(null)
				setError('Session expired')
			}
		} catch (error) {
			console.error('Refresh error:', error)
			setError(error instanceof Error ? error.message : 'Failed to refresh session')
			setUser(null)
		} finally {
			setLoading(false)
		}
	}, [loadUserFromCookie])

	const login = useCallback((returnUrl?: string) => {
		redirectToLogin(returnUrl)
	}, [])

	const lastIdentifiedRef = useRef<string | null>(null)
	useEffect(() => {
		function syncAnalyticsIdentity() {
			if (!hasAnalyticsConsent() || !posthog.__loaded) {
				lastIdentifiedRef.current = null
				return
			}
			if (user?.userId) {
				const isInternal = isInternalAnalyticsUser(user)
				posthog.register({ is_internal: isInternal })
				if (user.userId !== lastIdentifiedRef.current) {
					posthog.identify(user.userId, { email: user.email, role: user.role, is_internal: isInternal })
					lastIdentifiedRef.current = user.userId
				}
			} else if (lastIdentifiedRef.current) {
				posthog.reset()
				lastIdentifiedRef.current = null
			}
		}
		syncAnalyticsIdentity()
		window.addEventListener('cookie-consent-updated', syncAnalyticsIdentity)
		window.addEventListener('posthog-initialized', syncAnalyticsIdentity)
		return () => {
			window.removeEventListener('cookie-consent-updated', syncAnalyticsIdentity)
			window.removeEventListener('posthog-initialized', syncAnalyticsIdentity)
		}
	}, [user])

	// Use refs to avoid dependency cycles in intervals
	const userRef = useRef<UserDataCookie | null>(null)
	const refreshRef = useRef(refresh)

	// Keep refs updated
	useEffect(() => {
		userRef.current = user
	}, [user])

	useEffect(() => {
		refreshRef.current = refresh
	}, [refresh])

	const checkTokenExpiry = useCallback(() => {
		const currentUser = userRef.current
		if (!currentUser) return

		const now = Date.now()
		const fiveMinutesInMs = 5 * 60 * 1000

		if (currentUser.expiresAt - now <= fiveMinutesInMs) {
			console.debug('Token expiring soon, refreshing...')
			refreshRef.current()
		}
	}, [])

	// Initialization effect - runs only once on mount
	useEffect(() => {
		let mounted = true

		const initAuth = async () => {
			try {
				setLoading(true)

				const extractedFromUrl = extractAndSaveAuthFromUrl()
				const cookieUser = loadUserFromCookie()

				if (cookieUser) {
					setUser(cookieUser)
					setLoading(false)

					if (!extractedFromUrl) {
						verifyWithAuthService(true)
					}
				} else {
					await verifyWithAuthService(false)
				}
			} finally {
				if (mounted) {
					setLoading(false)
				}
			}
		}

		initAuth()

		return () => {
			mounted = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadUserFromCookie, verifyWithAuthService])

	// Interval effect for token refresh and event listeners
	useEffect(() => {
		let mounted = true

		const interval = setInterval(() => {
			if (!mounted) return

			const updatedUser = loadUserFromCookie()

			if (updatedUser) {
				checkTokenExpiry()
			}
		}, 30000)

		const handleStorageChange = (e: StorageEvent) => {
			if (e.key?.startsWith('extenshi_auth_event_')) {
				setTimeout(() => {
					if (mounted) {
						loadUserFromCookie()
					}
				}, 100)
			}
		}

		const handleFocus = () => {
			if (mounted) {
				loadUserFromCookie()
			}
		}

		window.addEventListener('storage', handleStorageChange)
		window.addEventListener('focus', handleFocus)

		return () => {
			mounted = false
			clearInterval(interval)
			window.removeEventListener('storage', handleStorageChange)
			window.removeEventListener('focus', handleFocus)
		}
	}, [loadUserFromCookie, checkTokenExpiry])

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				error,
				isAuthenticated,
				signOut,
				refresh,
				login,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth(): AuthContextValue {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}
